import {
  Badge,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardSection,
  CardSectionDescription,
  CardSectionTitle,
  CardTitle,
} from '@apollo/orbit';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { LocalTimestamp } from 'src/components/timestamp/Timestamp';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { useGraphRef } from '../../hooks/useGraphRef';

interface Props {
  // Ideally this would be a fragment.
  data: GraphQLTypes.VariantGeneralSettingsPageQuery;
}

export const ContractVariantSettingsCard = ({ data }: Props) => {
  const graphRef = useGraphRef();
  const location = useLocation();
  const currentVariant = data.graph?.variant;
  const createdAt = currentVariant?.activeSchemaPublish?.schema.createdAt
    ? new Date(currentVariant.activeSchemaPublish.schema.createdAt)
    : null;
  const lastUpdated = currentVariant?.activeSchemaPublish?.publishedAt
    ? new Date(currentVariant.activeSchemaPublish?.publishedAt)
    : null;

  const includeTags = currentVariant?.contractFilterConfig?.include;
  const excludeTags = currentVariant?.contractFilterConfig?.exclude;

  const version = currentVariant?.activeSchemaPublish?.schema.hash;
  const lastUpdateTimestamp = lastUpdated ?? createdAt;

  const canManageBuildConfig = data?.graph?.roles?.canManageBuildConfig;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contract configuration</CardTitle>
        <CardDescription>
          This is a contract variant, derived from the{' '}
          <b>
            {graphRef?.graphId}@{currentVariant?.sourceVariant?.name}
          </b>{' '}
          source variant.
        </CardDescription>
        <Tooltip
          disabled={canManageBuildConfig}
          label="You do not have permission to edit contracts"
        >
          <Button
            variant="secondary"
            isDisabled={!canManageBuildConfig}
            as={Link}
            to={setOverlayVisibleLocation({
              location,
              overlayName: Config.modals.editContractConfigWizard,
              additionalSearchParameters: {
                [Config.queryParameters.ContractVariant]:
                  graphRef?.graphVariant,
                [Config.queryParameters.SourceVariant]:
                  currentVariant?.sourceVariant?.name,
              },
            })}
          >
            Edit configuration
          </Button>
        </Tooltip>
      </CardHeader>
      <CardBody>
        <CardSection>
          <CardSectionTitle>Last updated</CardSectionTitle>
          {lastUpdateTimestamp && (
            <CardSectionDescription className="flex items-center">
              <LocalTimestamp value={lastUpdateTimestamp} />
              {version && (
                <li className="ml-4 text-sm">
                  <span className="text-base">{version.slice(0, 6)}</span>
                </li>
              )}
            </CardSectionDescription>
          )}
        </CardSection>
        <div className="flex gap-6">
          <CardSection>
            <CardSectionTitle>Included tags</CardSectionTitle>
            <CardSectionDescription>
              <div className="flex gap-2">
                {includeTags && includeTags.length > 0
                  ? includeTags.map((tag) => <Badge key={tag}>{tag}</Badge>)
                  : 'None'}
              </div>
            </CardSectionDescription>
          </CardSection>
          <CardSection>
            <CardSectionTitle>Excluded tags</CardSectionTitle>
            <CardSectionDescription>
              <div className="flex gap-2">
                {excludeTags && excludeTags.length > 0
                  ? excludeTags.map((tag) => <Badge key={tag}>{tag}</Badge>)
                  : 'None'}
              </div>
            </CardSectionDescription>
          </CardSection>
        </div>
        <CardSection>
          <CardSectionTitle>Hide unreachable types</CardSectionTitle>
          <CardSectionDescription>
            <Badge>
              {Boolean(
                data.graph?.variant?.contractFilterConfig?.hideUnreachableTypes,
              ).toString()}
            </Badge>
          </CardSectionDescription>
        </CardSection>
      </CardBody>
    </Card>
  );
};
